import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  AddSourceToExperienceMutation,
  AddSourceToExperienceMutationVariables,
} from './useAddSource.generated';

const query = gql`
  mutation AddSourceToExperience($input: AddSourceToExperienceInput!) {
    addSourceToExperience(input: $input) {
      kind
      id
      title
      originalTitle
      color
      syncedAt
      error
    }
  }
`;

export const useAddSource = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    AddSourceToExperienceMutation,
    QueryError,
    AddSourceToExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        AddSourceToExperienceMutation,
        AddSourceToExperienceMutationVariables
      >(query, variables),
    // The experience query key is a prefix of its events key, so this single
    // invalidation refreshes both experience.feeds and the sourced events
    // the server materialized during the synchronous first sync.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
      }),
  });

  const addSource = (
    uri: string,
    options?: MutateOptions<
      AddSourceToExperienceMutation,
      QueryError,
      AddSourceToExperienceMutationVariables
    >
  ) => mutate({ input: { experienceId: experience.id, uri } }, options);

  return { addSource, ...rest };
};
